import React, { lazy } from "react";
import { Navigate, Outlet } from "react-router-dom";
import Loadable from "../layouts/full/shared/loadable/Loadable";
import HotelReservationsOverview from "src/components/reservations/my-reservations/my-reservations-overview/MyReservationsOverview";
import ViewReservationsOverview from 'src/components/reservations/view-reservations/view-reservations-overview/ViewReservationsOverview';
import PermissionGuard from "src/permissions/PermissionGuard";
import { routes } from "src/utils/common";
import ReservationsClientVoucher from "src/components/reservations/client-voucher/ReservationsClientVoucher";
import { permissionList } from "src/utils/permissions";

/* ***Layouts**** */
const FullLayout = Loadable(lazy(() => import('../layouts/full/FullLayout')));
const BlankLayout = Loadable(lazy(() => import('../layouts/blank/BlankLayout')));

// Dashboard
const Dashboard = Loadable(lazy(() => import('../views/dashboard/Dashboard')));
const Error = Loadable(lazy(() => import('../views/authentication/Error')));
const Maintenance = Loadable(lazy(() => import('../views/authentication/Maintenance')));
const Reservations = Loadable(lazy(() => import('../views/reservations/NewReservations')));
const ReservationsSummary = Loadable(lazy(() => import('../views/reservations/MyReservations')));
const ViewReservationsSummary = Loadable(lazy(() => import('../views/reservations/ViewReservations')));
const Location = Loadable(lazy(() => import('../views/locations/Locations')));
const Agents = Loadable(lazy(() => import('../views/agents/Agents')));
const B2BUsers = Loadable(lazy(() => import('../views/b2b-users/B2BUsers')));
const B2BMarkups = Loadable(lazy(() => import('../views/b2b-markups/B2BMarkup')));
const Hotels = Loadable(lazy(() => import('../views/hotels/Hotels')));
const Service = Loadable(lazy(() => import('../views/services/Service')));
const B2BAgentSetup = Loadable(lazy(() => import('../views/b2b-agent-setup/B2BAgentSetup')));
const Chat = Loadable(lazy(() => import('../views/chat/Chat')));

const Router = [
  {
    path: routes.root,
    element: <FullLayout />,
    children: [
      { path: routes.root, element: <Navigate to={routes.dashboard} /> },
      {
        path: routes.dashboard,
        element: (
          <PermissionGuard permission={permissionList.dashboard}>
            <Dashboard />
          </PermissionGuard>
        ),
      },
      {
        path: routes.locations,
        element: (
          <PermissionGuard permission={permissionList.locations}>
            <Location />
          </PermissionGuard>
        ),
      },
      {
        path: routes.agents,
        element: (
          <PermissionGuard permission={permissionList.agents}>
            <Agents />
          </PermissionGuard>
        ),
      },

      {
        path: routes.hotels,
        element: (
          <PermissionGuard permission={permissionList.hotels}>
            <Hotels />
          </PermissionGuard>
        )
      },

      {
        path: routes.services,
        element: (
          <PermissionGuard permission={permissionList.services}>
            <Service />
          </PermissionGuard>
        )
      },
      {
        path: routes.chat,
        element: (
          <PermissionGuard permission={permissionList.chat}>
            <Chat />
          </PermissionGuard>
        ),
      },
      {
        path: routes.b2bUsers,
        element: (
          <PermissionGuard permission={permissionList.b2bUsers}>
            <B2BUsers />
          </PermissionGuard>
        )
      },
      {
        path: routes.b2bAgentSetup,
        element: (
          <PermissionGuard permission={permissionList.b2bAgentSetup}>
            <B2BAgentSetup />
          </PermissionGuard>
        )
      },
      {
        path: routes.b2bMarkups,
        element: (
          <PermissionGuard permission={permissionList.b2bMarkups}>
            <B2BMarkups />
          </PermissionGuard>
        )
      },
      {
        path: routes.reservations.base,
        element: <Outlet />,
        children: [
          {
            path: routes.reservations.new,
            element: (
              <PermissionGuard permission={permissionList.b2bNewReservations}>
                <Reservations />
              </PermissionGuard>
            ),
          },
          {
            path: routes.reservations.allClientVouchers,
            element: (
              <PermissionGuard permission={permissionList.b2bMyReservations}>
                <ReservationsClientVoucher />
              </PermissionGuard>
            ),
          },
          {
            path: routes.reservations.singleClientVoucher,
            element: (
              <PermissionGuard permission={permissionList.b2bMyReservations}>
                <ReservationsClientVoucher />
              </PermissionGuard>
            ),
          },
          {
            path: routes.reservations.my,
            element: (
              <PermissionGuard permission={permissionList.b2bMyReservations}>
                <ReservationsSummary />
              </PermissionGuard>
            ),
            children: [
              {
                path: routes.reservations.overview,
                element: <HotelReservationsOverview />,
              }
            ],
          },
          {
            path: 'view',
            element: (
              <PermissionGuard permission={permissionList.backOfficeViewReservations}>
                <ViewReservationsSummary />
              </PermissionGuard>
            ),
            children: [
              { path: ':id/overview', element: <ViewReservationsOverview /> }
            ]
          },
        ],
      },
      { path: "*", element: <Navigate to={routes.auth.error404} /> },
    ],
  },
  {
    path: routes.root,
    element: <BlankLayout />,
    children: [
      { path: routes.auth.error404, element: <Error /> },
      { path: routes.auth.maintenance, element: <Maintenance /> },
      { path: "*", element: <Navigate to={routes.auth.error404} /> },
    ],
  },
];

export default Router;
